import { createMemo, Show } from 'solid-js';
import { Switch, Meter } from '@components';
import { useSettings, useLicense } from '@util/context';

export default function Basic() {
	const { settings, saveSettings } = useSettings();
	const { isLocked, shakePromo } = useLicense();

	/**
	 * Handle Admin SPA toggle with full page reload.
	 * This breaks out of iframe and reloads to apply/remove the iframe wrapper.
	 */
	const handleAdminSpaToggle = (value) => {
		const previousValue = settings.enable_admin_spa;
		settings.enable_admin_spa = value;

		// Only reload if value actually changed after successful save
		if (previousValue !== value) {
			saveSettings().then((result) => {
				if (result?.success) {
					setTimeout(() => {
						window.top.location.reload();
					}, 300);
				}
			});
		}
	};

	const meterSpeed = createMemo(() => {
		if (settings.enable_navigation && settings.enable_prefetch) {
			return 100;
		}
		if (settings.enable_navigation) {
			return 77.5;
		}
		return 27.5;
	});

	return (
		<section class="ap-flex ap-flex-col min-[1080px]:ap-flex-row ap-gap-6 sm:ap-gap-10 ap-justify-between ap-flex-wrap">
			<div class="ap-flex-1 ap-min-w-0">
				<div class="ap-space-y-6 sm:ap-space-y-10">
					<Switch
						data-tour="instant-navigation"
						value={settings.enable_navigation}
						onChange={(v) => settings.enable_navigation = v}
						subtitle="Smooth navigation, forms, and search. Everything without refresh."
						instantSave={true}
						tooltip={
							<>
								<strong>Enables SPA (Single Page Application) mode</strong> for your entire frontend.<br /><br />
								• <strong>Smooth navigation</strong>: pages, posts, and products load instantly<br />
								• <strong>Smooth form submissions</strong>: contact forms, checkouts without refresh<br />
								• <strong>Smooth search</strong>: search results appear seamlessly<br /><br />
								Everything works without a full page reload, creating an app-like experience.<br /><br />
								<a href="https://arraystory.com/docs/ajaxpress/instant-navigation" target="_blank" class="ap-text-indigo-600 hover:ap-text-indigo-700 ap-underline">Learn more</a>
							</>
						}
					>
						Enable SPA for Frontend
					</Switch>

					<Switch
						value={settings.enable_admin_spa}
						onChange={(v) => isLocked() ? shakePromo() : handleAdminSpaToggle(v)}
						subtitle="Smooth admin navigation and form saves. No refresh needed."
						locked={isLocked()}
						tooltip={
							<>
								<strong>Enables SPA mode for WordPress admin area.</strong><br /><br />
								• <strong>Smooth navigation</strong>: admin pages load instantly<br />
								• <strong>Smooth form submissions</strong>: settings save without refresh<br />
								• <strong>Faster workflows</strong>: seamless editing experience<br /><br />
								Everything works without a full page reload.<br /><br />
								<em>Beta: May not work with all plugins.</em>
							</>
						}
						badge={<span class="ap-text-[10px] ap-font-bold ap-px-1.5 ap-py-0.5 ap-rounded ap-uppercase" style="background: #fef3c7; color: #b45309">Beta</span>}
					>
						Enable SPA for Admin Dashboard
					</Switch>

					<Show when={settings.enable_navigation}>
						<Switch
							data-tour="prefetch-toggle"
							value={settings.enable_prefetch}
							onChange={(v) => isLocked() ? shakePromo() : settings.enable_prefetch = v}
							subtitle="Prefetch pages when users hover over links for instant loading."
							locked={isLocked()}
							tooltip={
								<>
									<strong>Prefetches page content</strong> when users hover over links.<br /><br />
									When enabled, AjaxPress will fetch page content in the background as users hover over links. When they click, the page loads instantly.<br /><br />
									<em>Significantly improves perceived performance with minimal bandwidth cost.</em>
								</>
							}
						>
							Enable Prefetch
						</Switch>

						<Switch
							data-tour="disable-admins"
							value={settings.disable_for_admins}
							onChange={(v) => isLocked() ? shakePromo() : settings.disable_for_admins = v}
							subtitle="Logged in admins see normal page loads on frontend."
							locked={isLocked()}
							tooltip={
								<>
									<strong>Disables SPA mode for administrators</strong> on the frontend.<br /><br />
									When enabled, logged in admin users will experience normal page reloads instead of SPA navigation. Useful for debugging or testing.<br /><br />
									<em>Regular visitors still get the full SPA experience.</em>
								</>
							}
						>
							Load Frontend Normally for Admins
						</Switch>

						<Switch
							value={settings.block_keyboard_reload}
							onChange={(v) => isLocked() ? shakePromo() : settings.block_keyboard_reload = v}
							subtitle="Block Ctrl+R, Cmd+R, and F5 from reloading the page."
							locked={isLocked()}
							tooltip={
								<>
									<strong>Block keyboard reload shortcuts</strong> on the frontend.<br /><br />
									Prevents visitors from accidentally reloading the page using keyboard shortcuts like Ctrl+R, Cmd+R, or F5.<br /><br />
									<em>Useful for radio stations and media sites where reload interrupts playback.</em>
								</>
							}
						>
							Block Keyboard Reload
						</Switch>
					</Show>
				</div>
			</div>

			<div class="ap-hidden min-[1080px]:ap-block ap-w-full min-[1080px]:ap-w-auto ap-max-w-sm ap-min-w-[220px] ap-flex-shrink-0 ap-pb-[100px]">
				<div class="ap-w-full ap-max-w-sm ap-relative min-[1080px]:ap-sticky ap-top-24 ap-self-start ap-mx-auto">
					<div class="ap-flex ap-items-center ap-justify-center ap-w-full">
						<Meter speed={meterSpeed()} />
					</div>
				</div>
			</div>
		</section>
	);
}
